import { ContextMenu, LockIcon, PinIcon, RoomIcon } from '@bp/ui-components';
import classNames from 'classnames';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { MouseEvent, useCallback } from 'react';
import { useDebounceCallback } from 'usehooks-ts';
import { useTeachingBlockCardMenu } from '../../../hooks/useTeachingBlockCardMenu';
import { useTeachingBlockStore } from '../Contexts/TeachingBlockContext';
import { TeachingBlockCardType } from '../types';
import styles from './GridCard.module.scss';

type GridCardProps = {
  card: TeachingBlockCardType;
  left: string;
  width: string;
  height: string;
  top: string;
  background: string;
  textColor: string;
  onCardClick: (event: MouseEvent<HTMLDivElement>, card: TeachingBlockCardType) => void;
};

export const GridCard = observer(
  ({ card, left, width, height, top, background, textColor, onCardClick }: GridCardProps) => {
    const store = useTeachingBlockStore();

    const isPicked =
      card.uuid === store.pickedCard?.card.uuid ||
      (!!card.virtualChildren && !!card.virtualChildren.find((uuid) => card.uuid.includes(uuid)));

    const isPinned =
      card.uuid === store.pinnedCard?.uuid ||
      (!!card.virtualChildren && !!card.virtualChildren.find((uuid) => card.uuid.includes(uuid)));

    const isLocked = card.locked ?? false;

    const hasWarnings = !!card.warnings && card.warnings.length > 0;
    const hasAvailabilityWarnings = !!card.availabilityWarnings && card.availabilityWarnings.length > 0;
    const showWarnings = store.highlightMode.includes('warnings') && (hasWarnings || hasAvailabilityWarnings);

    function onCardEditOpen() {
      store.setCardDurationModal({
        isOpen: true,
        card: card ?? undefined,
      });
    }

    const { menu, hasRooms } = useTeachingBlockCardMenu({
      card: card,
      styles,
      hasWarnings,
      hasAvailabilityWarnings,
      isPicked,
      isPinned,
      onCardEditOpen,
    });

    const menuClasses = classNames(styles.menu, { [styles['has-warning']]: showWarnings });

    const cardClasses = classNames(styles['grid-card'], {
      [styles.picked]: isPicked,
      [styles.pinned]: isPinned,
      [styles.locked]: isLocked,
      [styles.warning]: showWarnings,
      [styles.room]: store.highlightMode.includes('rooms') && !hasRooms,
      // [styles['high-light']]:
      //   pinboardStore.controlMode.isActive &&
      //   pinboardStore.controlMode.highlights.length !== 0 &&
      //   pinboardStore.controlMode.highlights.includes(card.uuid),
      // [styles['low-light']]:
      //   pinboardStore.controlMode.isActive &&
      //   pinboardStore.controlMode.highlights.length !== 0 &&
      //   !pinboardStore.controlMode.highlights.includes(card.uuid),
    });

    const debounceOnCardHover = useDebounceCallback(() => {
      store.selectCard(toJS(card));
    }, 500);

    const onMouseEnter = useCallback(() => {
      if (!store.selectedCard) {
        store.selectCard(toJS(card));
      } else {
        debounceOnCardHover();
      }
    }, [card, debounceOnCardHover, store]);

    const onMouseLeave = useCallback(() => {
      debounceOnCardHover.cancel();
    }, [debounceOnCardHover]);

    const onOpenChange = useCallback(() => {
      debounceOnCardHover.cancel();
      store.selectCard(toJS(card));
    }, [card, debounceOnCardHover, store]);

    return (
      <>
        <ContextMenu
          className={menuClasses}
          data={menu}
          parent={document.getElementById('teaching-block-grid')}
          onOpenChange={onOpenChange}
        >
          <div
            key={card.uuid}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={(e) => onCardClick(e, card)}
            title={card.label}
            className={cardClasses}
            style={{
              height,
              left: `round(nearest, ${left}, 1px)`,
              width: `round(nearest, ${width}, 1px)`,
              top,
              background,
              color: textColor,
            }}
          >
            <div className={styles.title}>{card.labelShort}</div>
            {!store.highlightMode.includes('noIcons') && (card.pinned || card.locked || !hasRooms) && (
              <div className={styles.info}>
                {card.pinned && <PinIcon className={styles.icon} />}
                {card.locked && <LockIcon className={styles.icon} />}
                {!hasRooms && <RoomIcon className={styles.icon} />}
              </div>
            )}
          </div>
        </ContextMenu>
      </>
    );
  },
);
